import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";
import { useCopy } from "../../context/CopyContext";
import Header from "../Header";
import Button from "../Button";
import MultiLineCopy from "../MultiLineCopy";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";
import Heading from "../Heading";

const MemberWelcomeView: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAppContext();
  const { getCopy } = useCopy();
  const [showItemsModal, setShowItemsModal] = useState(false);

  // Auto-continue after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log("5 second timer expired, continuing experience from MemberWelcomeView");
      // Continue to appropriate next step based on inline settings
      if (state.isSignup && state.settings.inlineSignUp) {
        navigate("/kiosk-sign-up");
      } else if (!state.isSignup && state.settings.inlineSignIn) {
        navigate("/kiosk-sign-in");
      } else {
        // Default: continue to reward method selection
        navigate("/reward-method-selection");
      }
    }, 5000);

    // Cleanup timer on unmount
    return () => clearTimeout(timer);
  }, [navigate, state.isSignup, state.settings.inlineSignUp, state.settings.inlineSignIn]);

  const handleBack = () => {
    if (state.isSignup) {
      // If coming from membership selection (signup flow), go back to membership selection
      navigate("/membership-selection");
    } else {
      // If coming from email capture (existing member), go back to email capture
      navigate("/email-capture");
    }
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  const handleKioskAction = () => {
    if (state.isSignup) {
      // New user - go to sign up form
      navigate("/kiosk-sign-up");
    } else {
      // Existing user - go to sign in form
      navigate("/kiosk-sign-in");
    }
  };

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="347:6170"
    >
      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-24">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              
              
               // This is a recognized member
              
               // Animate icon since user is recognized as member
              onItemsClick={handleItemsClick}
            />
          </div>
        </div>

        {/* Main Content Area - with 3-row grid */}
        <motion.div
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Row 1 - Main heading */}
          <div className="flex items-center justify-center">
            <div className="text-center w-full max-w-[920px]">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.6,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <Heading
                  title={state.isSignup ? "Great choice!" : "Hello, again!"}
                  subtitle={state.isSignup 
                    ? getCopy('memberWelcome.newMemberSubheading')
                    : getCopy('memberWelcome.existingMemberSubheading')
                  }
                  titleWeight="bold"
                />
              </motion.div>
            </div>
          </div>

          {/* Row 2-3 - QR Code and Help text combined */}
          <div className="row-span-2 flex flex-col justify-between">
            {/* QR Code - vertically centered in remaining space */}
            <div className="flex-1 flex items-center justify-center">
              <motion.div
                className="flex justify-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.8,
                  delay: 0.2,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <div className="w-[325px] h-[325px] rounded-xl bg-white p-4">
                  <img
                    src="/assets/images/icons/qr-code.png"
                    alt="QR Code for Rewards login"
                    className="w-full h-full object-contain"
                  />
                </div>
              </motion.div>
            </div>

            {/* Help text - at the very bottom */}
            <div className="flex justify-center pb-4">
              <motion.div
                className="text-center w-full max-w-[680px]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: 0.4,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <p className="font-brand-primary font-light text-[32px] leading-[42px] text-brand-text-primary">
                  {(state.isSignup ? state.settings.inlineSignUp : state.settings.inlineSignIn) ? (
                    <button 
                      onClick={handleKioskAction}
                      className="font-normal underline text-brand-text-primary transition-colors duration-200"
                    >
                      {state.isSignup ? "Sign up on Kiosk" : "Sign in on Kiosk"}
                    </button>
                  ) : (
                    <MultiLineCopy 
                      text={getCopy('memberWelcome.helpText')}
                      lineClassName="font-normal"
                    />
                  )}
                </p>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Bottom Section - Back button and footer */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Back Button */}
          <div className="flex justify-center w-full max-w-[282px]">
            <Button variant="tertiary" onClick={handleBack}>
              Back
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView isOpen={showItemsModal} onClose={handleCloseItemsModal} />
    </div>
  );
};

export default MemberWelcomeView;
